import React, { useState, useEffect } from 'react';
import { invoiceApi } from '../utils/api';
import { IconFile, IconCheckCircle } from './Icons';

export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    organizerId: '',
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchInvoices();
    fetchStats();
  }, [filters]);

  async function fetchInvoices() {
    setLoading(true);
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.organizerId) params.organizerId = filters.organizerId;

      const res = await invoiceApi.getAllInvoices(params);
      setInvoices(res.data || []);
    } catch (err) {
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchStats() {
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const res = await invoiceApi.getRevenueStats(params);
      setStats(res.data || {});
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }

  async function handleRegenerateInvoice(invoiceId) {
    if (!window.confirm('Regenerate this invoice PDF? This will replace the existing file.')) {
      return;
    }

    try {
      await invoiceApi.regenerateInvoice(invoiceId);
      alert('Invoice regenerated successfully');
      fetchInvoices();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to regenerate invoice');
    }
  }

  function handleDownloadInvoice(invoiceId) {
    invoiceApi.downloadInvoice(invoiceId);
  }

  function clearFilters() {
    setFilters({ startDate: '', endDate: '', organizerId: '' });
    setSearchTerm('');
  }

  // Filter invoices by search term
  const filteredInvoices = invoices.filter(inv => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      inv.invoice_number?.toLowerCase().includes(search) ||
      inv.event_name?.toLowerCase().includes(search) ||
      inv.organizer_name?.toLowerCase().includes(search) ||
      inv.organizer_email?.toLowerCase().includes(search)
    );
  });

  return (
    <div>
      <div className="card-header" style={{ marginBottom: 20 }}>
        <h2>Invoices</h2>
        <div style={{ fontSize: '0.85rem', color: '#8a9bb5' }}>
          Manage all event invoices
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
          <div className="card" style={{ padding: 16 }}>
            <div style={{ color: '#8a9bb5', fontSize: '0.75rem', marginBottom: 4 }}>TOTAL INVOICES</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 600, color: '#4a9eff' }}>
              {stats.total_invoices || 0}
            </div>
          </div>
          <div className="card" style={{ padding: 16 }}>
            <div style={{ color: '#8a9bb5', fontSize: '0.75rem', marginBottom: 4 }}>TOTAL REVENUE</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 600, color: '#5dd95d' }}>
              ${parseFloat(stats.total_revenue || 0).toFixed(2)} <span style={{ fontSize: '0.7rem', color: '#8a9bb5' }}>CAD</span>
            </div>
          </div>
          <div className="card" style={{ padding: 16 }}>
            <div style={{ color: '#8a9bb5', fontSize: '0.75rem', marginBottom: 4 }}>TOTAL TAX COLLECTED</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 600, color: '#e8a838' }}>
              ${parseFloat(stats.total_tax || 0).toFixed(2)} <span style={{ fontSize: '0.7rem', color: '#8a9bb5' }}>CAD</span>
            </div>
          </div>
          <div className="card" style={{ padding: 16 }}>
            <div style={{ color: '#8a9bb5', fontSize: '0.75rem', marginBottom: 4 }}>AVERAGE INVOICE</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 600, color: '#9d7ef2' }}>
              ${parseFloat(stats.average_invoice || 0).toFixed(2)} <span style={{ fontSize: '0.7rem', color: '#8a9bb5' }}>CAD</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card" style={{ marginBottom: 20, padding: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: '0.8rem', color: '#8a9bb5' }}>Search</label>
            <input
              type="text"
              placeholder="Invoice #, event, organizer..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                background: '#152232',
                border: '1px solid #2a3f56',
                borderRadius: 6,
                color: '#f0f2f5',
                fontSize: '0.85rem'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: '0.8rem', color: '#8a9bb5' }}>Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={e => setFilters(f => ({ ...f, startDate: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                background: '#152232',
                border: '1px solid #2a3f56',
                borderRadius: 6,
                color: '#f0f2f5',
                fontSize: '0.85rem'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: '0.8rem', color: '#8a9bb5' }}>End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={e => setFilters(f => ({ ...f, endDate: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                background: '#152232',
                border: '1px solid #2a3f56',
                borderRadius: 6,
                color: '#f0f2f5',
                fontSize: '0.85rem'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={clearFilters}
              className="btn btn-ghost btn-sm"
              style={{ width: '100%', padding: '8px 12px', fontSize: '0.85rem' }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="card">
        <div className="table-wrap">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#8a9bb5' }}>
              Loading invoices...
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#8a9bb5' }}>
              No invoices found
            </div>
          ) : (
            <table className="responsive-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Event</th>
                  <th>Organizer</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Tax</th>
                  <th>Total</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map(inv => (
                  <tr key={inv.id}>
                    <td data-label="Invoice #" style={{ fontFamily: 'monospace', color: '#4a9eff' }}>
                      {inv.invoice_number}
                    </td>
                    <td data-label="Event" style={{ fontWeight: 500 }}>
                      {inv.event_name}
                    </td>
                    <td data-label="Organizer" style={{ color: '#8a9bb5', fontSize: '0.85rem' }}>
                      <div>{inv.organizer_name}</div>
                      <div style={{ fontSize: '0.75rem' }}>{inv.organizer_email}</div>
                    </td>
                    <td data-label="Date" style={{ fontSize: '0.85rem' }}>
                      {new Date(inv.payment_date).toLocaleDateString()}
                    </td>
                    <td data-label="Amount" style={{ color: '#5dd95d' }}>
                      ${parseFloat(inv.amount_before_tax - inv.discount_amount).toFixed(2)}
                    </td>
                    <td data-label="Tax" style={{ color: '#e8a838' }}>
                      ${parseFloat(inv.tax_amount).toFixed(2)}
                    </td>
                    <td data-label="Total" style={{ fontWeight: 600, color: '#f0f2f5' }}>
                      ${parseFloat(inv.total_amount).toFixed(2)} CAD
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleDownloadInvoice(inv.id)}
                          className="btn btn-ghost btn-sm"
                          style={{ padding: '4px 12px', fontSize: '0.75rem', color: '#4a9eff' }}
                        >
                          <IconFile size="13px" style={{ marginRight: 4, display: 'inline-block', verticalAlign: 'middle' }} />Download
                        </button>
                        <button
                          onClick={() => handleRegenerateInvoice(inv.id)}
                          className="btn btn-ghost btn-sm"
                          style={{ padding: '4px 12px', fontSize: '0.75rem', color: '#e8a838' }}
                        >
                          <IconCheckCircle size="13px" style={{ marginRight: 4, display: 'inline-block', verticalAlign: 'middle' }} />Regenerate
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Results count */}
      {!loading && filteredInvoices.length > 0 && (
        <div style={{ marginTop: 12, fontSize: '0.85rem', color: '#8a9bb5', textAlign: 'center' }}>
          Showing {filteredInvoices.length} of {invoices.length} invoices
        </div>
      )}
    </div>
  );
}
